import * as React from 'react';

import type {ByodFilterMap, Row, RowType, WidgetConfig} from 'src/types/byod';

/** Where a new widget should be added (passed to `onAddWidget`). */
export type CanvasAddPosition = {
  /** Add into this existing row. */
  rowId?: string;
  /** Column index within `rowId`. */
  col?: number;
  /** Row-type hint (for new KPI vs viz rows). */
  rowType?: RowType;
  /** Add as a NEW row after this `rowOrder` index (inter-row "add report"). */
  afterIndex?: number;
};

/**
 * The controlled value driving a single `<Canvas>`. The parent owns ALL of it:
 * the layout state (`rowOrder`/`rows`/`widgets`), the mode (`editable`), the
 * per-tile data inputs (`dashboardId` + injected `filters`), and the change
 * handlers. Distributed via context so the canvas's many sub-components
 * (tiles, dividers, DnD pipeline) read it without prop-drilling — but it's
 * seeded entirely from `<Canvas>`'s props each render, so two `<Canvas>`
 * instances on one page stay fully isolated (no singleton store).
 *
 * Handlers are optional and only invoked when `editable` is true.
 */
export type CanvasContextValue = {
  // ── Controlled state ──
  rowOrder: string[];
  rows: Record<string, Row>;
  widgets: Record<string, WidgetConfig>;

  // ── Mode ──
  editable: boolean;

  // ── Per-tile data inputs ──
  /** Dashboard id (cache keying). */
  dashboardId: string | null;
  /**
   * Injected filters merged over EACH tile's saved `widget_filters_json` at
   * query time (low → high: tile saved < injected). For the builder this is the
   * timezone-stamped date window; for the embed it's the host's `globalPayload`
   * (which already carries the date window).
   */
  filters: ByodFilterMap;

  // ── Derived ──
  /** True when the dashboard is at `MAX_WIDGETS` (gates add affordances). */
  isAtWidgetLimit: boolean;

  // ── Change handlers (called only when `editable`) ──
  onMoveTile?: (
    fromRowId: string,
    toRowId: string,
    fromIndex: number,
    toIndex: number,
  ) => void;
  onMoveTileToNewRow?: (
    fromRowId: string,
    fromIndex: number,
    afterIndex: number,
    // New rows are always tile rows (never a section).
    newRowType: 'kpi' | 'viz',
  ) => void;
  onRemoveWidget?: (tileId: string) => void;
  /** Returns the new section row's id so the canvas can open it for inline edit. */
  onAddSection?: (afterIndex: number) => string | void;
  onRenameSection?: (rowId: string, label: string) => void;
  onRemoveSection?: (rowId: string) => void;
  /** Persist a widget_config patch (chart style / selected columns / pagination). */
  onUpdateWidget?: (tileId: string, patch: Record<string, unknown>) => void;
  /** Navigate to edit a widget (parent owns navigation). */
  onEditWidget?: (tileId: string) => void;
  /** Navigate to add a widget at `position` (parent owns navigation). */
  onAddWidget?: (position: CanvasAddPosition) => void;
  /** Trigger an export for a widget (parent owns the side effect). */
  onExport?: (tileId: string) => void;

  // NOTE: scroll-to-tile (`focusTileId` / `onFocusConsumed`) is intentionally
  // NOT in the context. It changes on every edit-navigation but is consumed only
  // by CanvasInner's scroll effect (never by a tile), so keeping it out of the
  // context value means a focus change doesn't re-render every tile. `<Canvas>`
  // passes it straight to CanvasInner as props.
};

const CanvasContext = React.createContext<CanvasContextValue | null>(null);

export const CanvasProvider = CanvasContext.Provider;

export function useCanvasContext(): CanvasContextValue {
  const ctx = React.useContext(CanvasContext);
  if (ctx === null) {
    throw new Error('useCanvasContext must be used within <Canvas>');
  }
  return ctx;
}
